import Button from "@/components/form/Button";
import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { motion } from "framer-motion";
import CashCtrlWordmark from "@/components/logos/CashCtrlWordmark";
import ArrowRightIcon from "@/components/icons/ArrowRightIcon";
import { useGameEndData } from "@/state/cacheStoreHooks";
import { useGameStore } from "@/state/gameStore";
import { GameOverReason } from "@/types/state";
import { GameEnd } from "@/types/api";

type Props = {
    exiting: boolean;
    onDismiss: () => void;
};

const contentVariants = {
    initial: { y: "100vh" },
    animate: {
        y: 0,
        transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] },
    },
    exit: {
        y: "100vh",
        transition: { duration: 0.2, ease: "easeInOut" },
    },
} as const;

function getGameOverHeadline(gameOverReason: GameOverReason, gameEndData: GameEnd) {
    if (gameOverReason === "rent") return gameEndData.gameOverRentHeadline;
    if (gameOverReason === "cash") return gameEndData.gameOverCashHeadline;
    return gameEndData.gameOverSadHeadline;
}

function getGameOverText(gameOverReason: GameOverReason, gameEndData: GameEnd) {
    if (gameOverReason === "rent") return gameEndData.gameOverRentText;
    if (gameOverReason === "cash") return gameEndData.gameOverCashText;
    return gameEndData.gameOverSadText;
}

const GameEndMessageModal = ({ exiting, onDismiss }: Props) => {
    const gameEndData = useGameEndData();
    const gameOverReason = useGameStore((state) => state.gameOverReason);
    const isGameOver = gameOverReason !== "none";

    return (
        <div className="absolute inset-0 size-full overflow-hidden flex flex-col justify-center px-4">
            <motion.div variants={contentVariants} initial="initial" animate={exiting ? "exit" : "animate"}>
                <DefaultContentContainer
                    className="relative p-4 flex flex-col"
                    shadow={false}
                    rounded="md"
                    theme="white"
                >
                    <div className="flex justify-center mt-2">
                        <CashCtrlWordmark />
                    </div>
                    <h1 className="font-bold leading-tight mb-1.5 mt-4 w-full text-center text-2xl text-secondary-500">
                        {isGameOver && gameEndData && getGameOverHeadline(gameOverReason, gameEndData)}
                        {!isGameOver && (
                            <span>
                                ✨ Happy 2046! ✨<br />
                                Alles durchgespielt!
                            </span>
                        )}
                    </h1>
                    {isGameOver && (
                        <p className="leading-tight text-center mt-3 font-bold text-secondary-300">
                            {gameEndData ? getGameOverText(gameOverReason, gameEndData) : ""}
                        </p>
                    )}

                    {!isGameOver && (
                        <>
                            <p className="leading-tight text-center mt-3 text-secondary-300">
                                Du bist{" "}
                                <strong>
                                    nicht broke,
                                    <br />
                                    glücklich
                                </strong>{" "}
                                & hast fast jede <br />
                                <strong>Trophäe</strong> mitgenommen 🏆
                            </p>
                            <p className="leading-tight text-center mt-3 text-secondary-300">
                                Alles erreicht – und das wichtigste
                                <br />
                                über Finanzen gelernt!
                            </p>
                        </>
                    )}
                    <Button theme="secondary" className="mt-4" onClick={onDismiss} content="between">
                        <div>Ergebnisse anzeigen</div>
                        <ArrowRightIcon />
                    </Button>
                </DefaultContentContainer>
            </motion.div>
        </div>
    );
};

export default GameEndMessageModal;
